import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Loader2,
  Banknote,
  Wallet,
  CheckCircle2,
  XCircle,
  Hourglass,
} from "lucide-react";
import Pill from "@/components/Pill";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ReleasePaymentDialog from "@/components/ReleasePaymentDialog";

function fmtINR(n) {
  if (n == null || isNaN(n)) return "—";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n);
}
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function StatusBanner({ opa }) {
  if (opa.status === "rejected") {
    return (
      <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-4 flex items-start gap-3">
        <XCircle className="w-5 h-5 text-red-400 mt-0.5" />
        <div>
          <div className="text-sm font-semibold text-red-300">Rejected by {opa.rejected_by_name || "Admin"}</div>
          <div className="text-xs text-zinc-400 mt-0.5">{fmtDate(opa.rejected_at)}</div>
          {opa.rejection_reason && <div className="text-sm text-zinc-200 mt-2 italic">&ldquo;{opa.rejection_reason}&rdquo;</div>}
        </div>
      </div>
    );
  }
  if (opa.released_at) {
    return (
      <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4 flex items-start gap-3">
        <Banknote className="w-5 h-5 text-emerald-400 mt-0.5" />
        <div>
          <div className="text-sm font-semibold text-emerald-300">Payment Released by {opa.released_by_name || "Admin"}</div>
          <div className="text-xs text-zinc-400 mt-0.5">{fmtDate(opa.released_at)}</div>
        </div>
      </div>
    );
  }
  if (opa.status === "approved") {
    return (
      <div className="rounded-lg border border-sky-500/30 bg-sky-500/5 p-4 flex items-start gap-3">
        <CheckCircle2 className="w-5 h-5 text-sky-400 mt-0.5" />
        <div>
          <div className="text-sm font-semibold text-sky-300">Approved by {opa.approved_by_name || "Admin"}</div>
          <div className="text-xs text-zinc-400 mt-0.5">{fmtDate(opa.approved_at)}</div>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 flex items-start gap-3">
      <Hourglass className="w-5 h-5 text-amber-400 mt-0.5" />
      <div>
        <div className="text-sm font-semibold text-amber-300">Awaiting Admin Approval</div>
        <div className="text-xs text-zinc-400 mt-0.5">Requested {fmtDate(opa.created_at)}</div>
      </div>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-0.5">{label}</div>
      <div className={`text-sm ${mono ? "font-mono" : ""} text-zinc-200`}>
        {value || <span className="text-zinc-600">—</span>}
      </div>
    </div>
  );
}

export default function OPADetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [opa, setOpa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [releaseOpen, setReleaseOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/opa-requests/${id}`);
      setOpa(data);
    } catch (e) {
      toast.error("OPA request not found");
      navigate("/opa");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-zinc-500 text-sm" data-testid="opa-detail-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading OPA request…
      </div>
    );
  }
  if (!opa) return null;

  const isAdmin = user?.role === "admin";
  const canRelease = isAdmin && opa.status === "approved" && !opa.released_at;
  const linkedTask = opa.task;
  const linkedIssue = opa.issue;
  const backTarget = linkedTask ? `/tasks/${linkedTask.id}` : linkedIssue ? `/issues/${linkedIssue.id}` : "/opa";

  return (
    <div className="space-y-6" data-testid="opa-detail-page">
      <div>
        <Link
          to={backTarget}
          className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors"
          data-testid="opa-detail-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
      </div>

      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-overline mb-2 flex items-center gap-2">
            <Wallet className="w-3.5 h-3.5" />
            <span className="font-mono normal-case tracking-normal text-emerald-400" data-testid="opa-detail-code">
              {opa.code}
            </span>
          </div>
          <h1
            className="text-3xl font-semibold tracking-tight text-white"
            style={{ fontFamily: "Outfit" }}
            data-testid="opa-detail-amount"
          >
            {fmtINR(opa.amount)}
          </h1>
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            {opa.status === "approved" && !opa.released_at && <Pill kind="status" value="Approved" />}
            {opa.released_at && (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/20">
                <Banknote className="w-3 h-3" /> Released
              </span>
            )}
            {opa.status === "pending" && <Pill kind="status" value="Open" />}
            {opa.status === "rejected" && <Pill kind="status" value="Closed" />}
            <span className="text-xs text-zinc-500">Requested by {opa.requested_by_name || "—"}</span>
          </div>
        </div>
        {canRelease && (
          <Button
            onClick={() => setReleaseOpen(true)}
            className="bg-emerald-500 text-black hover:bg-emerald-400 font-medium"
            data-testid="opa-detail-release-btn"
          >
            <Banknote className="w-4 h-4 mr-1.5" /> Release Payment
          </Button>
        )}
      </header>

      <StatusBanner opa={opa} />

      <section className="bg-zinc-900 border border-white/10 rounded-lg p-5">
        <div className="label-overline mb-3">Request Details</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
          <Row label="OPA ID" value={opa.code} mono />
          <Row label="Amount" value={fmtINR(opa.amount)} mono />
          <Row label="Created" value={fmtDate(opa.created_at)} />
          {opa.purpose && (
            <div className="col-span-2 md:col-span-3">
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-0.5">Purpose</div>
              <div className="text-sm text-zinc-200 whitespace-pre-wrap">{opa.purpose}</div>
            </div>
          )}
        </div>
      </section>

      {opa.payment && (
        <section className="bg-zinc-900 border border-emerald-500/20 rounded-lg p-5" data-testid="opa-detail-payment">
          <div className="label-overline mb-3 text-emerald-300 flex items-center gap-2">
            <Banknote className="w-3.5 h-3.5" /> Bank Transaction
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
            <Row label="Transaction ID" value={opa.payment.transaction_id} mono />
            <Row label="Bank" value={opa.payment.bank_name} />
            <Row label="Payment Mode" value={opa.payment.payment_mode} />
            <Row label="Transfer Date" value={opa.payment.transfer_date} mono />
            <Row label="Released On" value={fmtDate(opa.released_at)} />
            <Row label="Released By" value={opa.released_by_name} />
            {opa.payment.remarks && (
              <div className="col-span-2 md:col-span-3">
                <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-0.5">Remarks</div>
                <div className="text-sm text-zinc-200 whitespace-pre-wrap">{opa.payment.remarks}</div>
              </div>
            )}
          </div>
        </section>
      )}

      {(linkedTask || linkedIssue) && (
        <section className="bg-zinc-900 border border-white/10 rounded-lg p-5">
          <div className="label-overline mb-3">{linkedTask ? "Linked Task" : "Linked Ticket"}</div>
          <Link
            to={linkedTask ? `/tasks/${linkedTask.id}` : `/issues/${linkedIssue.id}`}
            className="block rounded-md border border-white/10 p-3 bg-zinc-950 hover:border-white/30 transition-colors"
            data-testid="opa-detail-linked"
          >
            <div className={`font-mono text-xs ${linkedTask ? "text-sky-400" : "text-amber-400"} hover:underline`}>
              {(linkedTask || linkedIssue).code}
            </div>
            <div className="text-white font-medium mt-1">{(linkedTask || linkedIssue).subject}</div>
            <div className="flex gap-2 flex-wrap mt-2">
              <Pill kind="priority" value={(linkedTask || linkedIssue).priority} />
              <Pill kind="status" value={(linkedTask || linkedIssue).status} />
            </div>
          </Link>
        </section>
      )}

      <ReleasePaymentDialog
        opa={releaseOpen ? opa : null}
        onClose={() => setReleaseOpen(false)}
        onReleased={(updated) => setOpa(updated)}
      />
    </div>
  );
}
